import { SDK_Align, strings } from "@quickpoint.me/shared";
import { type ReactElement, useContext } from "react";
import styled, { ThemeProvider } from "styled-components";
import { elementAlign, opacityHelper } from "../../../lib/cssHelpers";
import {
	CurrentFilter,
	FilterColors,
	FilterFonts,
} from "../../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../../state/page";

import { useRecoilValue } from "recoil";
import type { BulletProps } from "../../../@types/viewerati";
import type { ElementProps } from "../../../functionalComponents/RenderPage";
import { mainCssRules } from "../../../lib/cssTemplates";
import modifierStyled from "../../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../../state/accessibility";
import { ListRunContext } from "../../../lib/listRuns";
import { IsInFlexBoxContext } from "../../displayElements/Cell";

const StyledIndentedBullet = styled.div<
	BulletProps & {
		$fontMultiplier: number;
		$baseFontSizeRaw: number;
		$inListRun?: boolean;
	}
>`
  width: ${(props) => (props.$isInPortrait ? "100%" : "66%")}; // this aligns the bullets
  padding-left: 16%;
  display: inline-flex;
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  margin-inline-start: ${(props) =>
		(props.theme.space ? props.theme.space : 3) * props.$baseFontSize}px;
  li span {
    position: relative;
    left: ${(props) => {
			const space =
				(props.theme.space ? props.theme.space / 10 : 0) * props.$baseFontSize;
			return `${space}px`;
		}}
  }

   ul {
    margin-block-end: 0;
    margin-block-start: 0;
    ${(props) =>
			props.theme.align !== "start" ? "padding-inline-start: 0;" : ""}
    list-style: circle;
    li::marker {
      color: ${(props) => props.theme.markerColor};

    }
    li {
      span {
        text-wrap: auto ;
      }
    }
  }

  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`}
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  ${(props) => `${modifierStyled("*:", props)}`}
  /* inside a list run the root IS the nested <li> (see listRuns.tsx) */
  ${(props) =>
		props.$inListRun
			? `
    display: list-item;
    width: 100%;
    margin-block: 0;
    margin-inline-start: 0;
    padding-inline-start: 0;
    padding-left: 0;
    text-align: start;
    &::marker {
      color: ${props.theme.markerColor};
    }
    & > span {
      text-wrap: auto;
      position: relative;
      left: ${(props.theme.space ? props.theme.space / 10 : 0) * props.$baseFontSize}px;
    }
  `
			: ""}
`;

const IndentedBullet = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const inListRun = useContext(ListRunContext);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const pageSize = useRecoilValue(PageSizeAtom);
	const tapped = elementData.tapped;
	let filter = useRecoilValue(CurrentFilter).bullet;

	if (!pageSize.landscape && filter.align !== SDK_Align.End) {
		filter = structuredClone(filter);
		filter.align = SDK_Align.Start;
	}

	const baseFontSize = pageSize.baseFontSize;

	if (!elementData.content) return <></>;

	return (
		<ThemeProvider theme={filter}>
			<StyledIndentedBullet
				as={inListRun ? "li" : undefined}
				$inListRun={inListRun}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				className={`${strings.css.pageElement}  bullet`}
				data-atom-id={elementData.id}
			>
				{inListRun ? (
					<span>{elementData.content}</span>
				) : (
					<ul className="pseudo">
						<li>
							<span>{elementData.content}</span>
						</li>
					</ul>
				)}
			</StyledIndentedBullet>
		</ThemeProvider>
	);
};

export default IndentedBullet;
